import { useRef } from 'react';

import { useT } from '@/i18n';

import styles from './Home.module.css';
import shared from '@/pages/pages.module.css';
import { fileKind } from '@/services/homeKinds';
import { formatBytes } from '@/lib/format';

import { AppIcon } from './AppIcons';
import { Button } from './Button';
import { BridgeMark, CheckIcon, CloseIcon, EyeIcon, EyeOffIcon, PaperclipIcon } from './Icons';
import { ErrorNote } from './ErrorNote';
import { PasswordPlate, PlateButton } from './PasswordPlate';
import type { SecretFormProps } from './SecretForm.types';
import { percent } from './useFileDownload';

/** The panel for a one-time secret: one field for the text, and under it a file, the way to attach one or the file itself on a plate. */
export function SecretForm({ draft, head, dragging, dragProps, uploading, uploadProgress, cancelling, error, canCreate, options, onSubmit, onCancelUpload }: SecretFormProps): React.JSX.Element {
  const {
    text,
    setText,
    masked,
    toggleMasked,
    password,
    passwordShown,
    togglePasswordShown,
    file,
    setFile,
    preview,
    brokenPreview,
    setBrokenPreview,
    validationError,
    textarea,
    pickFile,
    onPaste,
    unplate,
    onKeyDown,
  } = draft;
  const t = useT();
  const fileInput = useRef<HTMLInputElement>(null);
  const fileMeta = file === null ? '' : cancelling ? t('secretForm.cancelling') : uploadProgress !== null ? t('secretForm.uploading', { percent: percent(uploadProgress) }) : [fileKind(file.name), formatBytes(file.size)].filter(Boolean).join(' · ');
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      {head}
      {/* one field for the secret: text on top; under it a file — the way to attach one, or the file itself on a plate */}
      <div className={[styles.drop, styles.dropApp, styles.dropSecret, dragging ? styles.dropActive : ''].join(' ')} {...dragProps}>
        <label htmlFor="secret" className="visually-hidden">
          {t('secretForm.secretLabel')}
        </label>
        {password === null ? null : (
          <div className={styles.passwordRow}>
            <PasswordPlate
              password={password}
              shown={passwordShown}
              onToggle={togglePasswordShown}
              testId="password-plate"
            >
              <PlateButton label={t('secretForm.sendAsText')} onClick={unplate} testId="unplate">
                <CloseIcon size={14} />
              </PlateButton>
            </PasswordPlate>
          </div>
        )}
        <textarea
          ref={textarea}
          id="secret"
          className={[styles.textarea, styles.textareaPlain, password !== null ? styles.textareaNote : '', masked && password === null ? styles.textareaMasked : '', password === null && text !== '' && !/\s/.test(text) ? styles.textareaPassword : ''].join(' ')}
          placeholder={password === null ? t('secretForm.placeholder') : t('secretForm.placeholderNote')}
          value={text}
          onChange={(event) => {
            setText(event.target.value);
          }}
          onKeyDown={(event) => {
              onKeyDown(event, onSubmit);
            }}
          onPaste={onPaste}
          readOnly={uploading}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-testid="secret-input"
        />
        {text === '' || password !== null ? null : (
          <button
            type="button"
            className={styles.maskToggle}
            aria-label={masked ? t('secretForm.showText') : t('secretForm.hideText')}
            title={masked ? t('secretForm.showText') : t('secretForm.hideText')}
            aria-pressed={!masked}
            onClick={toggleMasked}
            data-testid="mask-toggle"
          >
            {masked ? <EyeIcon size={18} /> : <EyeOffIcon size={18} />}
          </button>
        )}
        <input ref={fileInput} type="file" className="visually-hidden" onChange={pickFile} tabIndex={-1} aria-label={t('secretForm.chooseFile')} data-testid="file-input" />
        {file ? (
          <div className={[styles.filePlate, uploadProgress === null ? styles.filePlateAdded : ''].join(' ')} data-testid="file-chip">
            {uploadProgress === null ? null : (
              <span
                className={styles.filePlateBar}
                style={{ width: `${String(Math.round(uploadProgress * 100))}%` }}
                role="progressbar"
                aria-label={t('secretForm.uploadingFile')}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.floor(uploadProgress * 100)}
              />
            )}
            <span className={styles.filePlateIconWrap}>
              {preview === null || preview === brokenPreview ? (
                <AppIcon kind="file" className={styles.filePlateIcon} />
              ) : (
                <img
                  className={styles.filePlateThumb}
                  src={preview}
                  alt=""
                  decoding="async"
                  onError={() => {
                    setBrokenPreview(preview);
                  }}
                  data-testid="file-thumb"
                />
              )}
              {uploadProgress === null ? (
                <span className={styles.filePlateCheck} aria-hidden="true">
                  <CheckIcon size={9} />
                </span>
              ) : null}
            </span>
            <span className={styles.filePlateName} title={file.name}>
              {file.name}
            </span>
            {uploadProgress === null ? (
              <span className={styles.filePlateStatus} role="status">
                {t('secretForm.added')}
              </span>
            ) : null}
            <span className={styles.filePlateMeta}>{fileMeta}</span>
            {uploading ? (
              <button
                type="button"
                className={styles.fileRemove}
                aria-label={t('secretForm.cancelUpload')}
                disabled={cancelling}
                onClick={onCancelUpload}
                data-testid="cancel-upload"
              >
                <CloseIcon size={16} />
              </button>
            ) : (
              <button type="button" className={styles.fileRemove} aria-label={t('secretForm.removeFile')} onClick={() => { setFile(null); }}>
                <CloseIcon size={16} />
              </button>
            )}
          </div>
        ) : (
          <div className={styles.attachBar}>
            <button type="button" className={styles.attachBtn} onClick={() => fileInput.current?.click()} data-testid="choose-file">
              <PaperclipIcon size={17} />
              {t('secretForm.attach')}
            </button>
            <span className={[styles.attachHint, styles.offPhone].join(' ')}>{t('secretForm.dropHint')}</span>
            <span className={styles.attachKeys} aria-hidden="true">
              ⌘/Ctrl + Enter
            </span>
          </div>
        )}
      </div>
      {validationError ? (
        <p className={shared.fieldError} role="alert">
          {validationError}
        </p>
      ) : error ? (
        <ErrorNote notice={error} />
      ) : null}
      <div className={[styles.actions, styles.formFoot].join(' ')}>
        <Button type="submit" className={styles.createBtn} icon={<BridgeMark size={15} />} disabled={!canCreate || uploading} data-testid="create">
          {t('common.createBridge')}
        </Button>
        {options}
      </div>
    </form>
  );
}
